import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Spinner,
  Alert,
  Badge,
  Input,
  Label,
  FormGroup
} from 'reactstrap';
import axios from 'axios';
import { BASE_URL } from '../constants/apiConfig';

const FinalizeCandidatesModal = ({ isOpen, toggle, onFinalized }) => {
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchEligibleCandidates();
      setSelectedCandidates([]);
      setSearchTerm('');
      setError('');
      setSuccess('');
    }
  }, [isOpen]);

  // Filter candidates based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredCandidates(candidates);
    } else {
      const searchLower = searchTerm.toLowerCase();
      const filtered = candidates.filter(candidate => {
        const idMatch = candidate.id.toString().includes(searchLower);
        const nameMatch = candidate.applicant_name.toLowerCase().includes(searchLower);
        const emailMatch = candidate.applicant_email.toLowerCase().includes(searchLower);
        const round1ScoreMatch = candidate.round1Score && candidate.round1Score.toString().includes(searchLower);
        const round2ScoreMatch = candidate.round2Score && candidate.round2Score.toString().includes(searchLower);
        
        return idMatch || nameMatch || emailMatch || round1ScoreMatch || round2ScoreMatch;
      });
      setFilteredCandidates(filtered);
    }
  }, [searchTerm, candidates]);

  const fetchEligibleCandidates = async () => {
    setLoading(true);
    setError('');
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(
        `${BASE_URL}/api/admin/dashboard/eligible-candidates`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setCandidates(response.data.data);
        setFilteredCandidates(response.data.data);
        if (response.data.data.length === 0) {
          setError('No eligible candidates found. Candidates must have completed both review rounds with scores.');
        }
      }
    } catch (err) {
      console.error('Error fetching eligible candidates:', err);
      setError(err.response?.data?.message || 'Failed to load eligible candidates');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCandidate = (candidateId) => {
    setSelectedCandidates(prev => {
      if (prev.includes(candidateId)) {
        return prev.filter(id => id !== candidateId);
      } else {
        return [...prev, candidateId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedCandidates.length === filteredCandidates.length) {
      setSelectedCandidates([]);
    } else {
      setSelectedCandidates(filteredCandidates.map(c => c.id));
    }
  };

  const handleFinalize = async (action) => {
    if (selectedCandidates.length === 0) {
      setError('Please select at least one candidate');
      return;
    }

    const actionText = action === 'select' ? 'shortlist' : 'reject';
    const confirmMessage = `Are you sure you want to ${actionText} ${selectedCandidates.length} candidate(s)? This will update the review status.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setProcessing(true);
    setError('');
    setSuccess('');

    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.post(
        `${BASE_URL}/api/admin/dashboard/finalize-candidates`,
        {
          candidateIds: selectedCandidates,
          action: action
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        const { processed, ineligibleCandidates, emailResults } = response.data.data;
        
        let successMsg = `Successfully ${action === 'select' ? 'shortlisted' : 'rejected'} ${processed} candidate(s).`;

        if (ineligibleCandidates && ineligibleCandidates.length > 0) {
          successMsg += ` ${ineligibleCandidates.length} candidate(s) were ineligible.`;
        }

        setSuccess(successMsg);
        setSelectedCandidates([]);
        
        // Refresh the list
        setTimeout(() => {
          fetchEligibleCandidates();
          if (onFinalized) {
            onFinalized();
          }
        }, 2000);
      }
    } catch (err) {
      console.error('Error finalizing candidates:', err);
      setError(err.response?.data?.message || 'Failed to shortlist applications');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl">
      <ModalHeader toggle={toggle}>
        Shortlist Application - Selection/Rejection
      </ModalHeader>
      <ModalBody>
        {error && <Alert color="danger">{error}</Alert>}
        {success && <Alert color="success">{success}</Alert>}

        <div className="mb-3">
          <Alert color="info">
            <strong>Eligibility Criteria:</strong> Only candidates who have completed both Round 1 and Round 2 reviews 
            with scores from all reviewers are eligible for finalization. Selecting candidates will update their review status to "Shortlisted".
            Use the separate "Send Annexure Email" feature to request additional documents from shortlisted candidates.
          </Alert>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2">Loading eligible candidates...</p>
          </div>
        ) : candidates.length === 0 ? (
          <Alert color="warning">
            No eligible candidates found at this time.
          </Alert>
        ) : (
          <>
            <div className="mb-3">
              <Input
                type="text"
                placeholder="Search by ID, Name, Email, or Score..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={processing}
              />
            </div>

            <div className="mb-3 d-flex justify-content-between align-items-center">
              <div>
                <strong>{filteredCandidates.length}</strong> eligible candidate(s) found
                {selectedCandidates.length > 0 && (
                  <span className="ms-3">
                    <Badge color="primary">{selectedCandidates.length} selected</Badge>
                  </span>
                )}
              </div>
              <FormGroup check>
                <Input
                  type="checkbox"
                  checked={selectedCandidates.length === filteredCandidates.length && filteredCandidates.length > 0}
                  onChange={handleSelectAll}
                  disabled={processing}
                />
                <Label check>Select All</Label>
              </FormGroup>
            </div>

            {filteredCandidates.length === 0 ? (
              <Alert color="info" className="text-center">
                No candidates found matching your search criteria.
              </Alert>
            ) : (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <Table striped bordered hover responsive>
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
                    <tr>
                      <th style={{ width: '50px' }}>
                        <Input
                          type="checkbox"
                          checked={selectedCandidates.length === filteredCandidates.length && filteredCandidates.length > 0}
                          onChange={handleSelectAll}
                          disabled={processing}
                        />
                      </th>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Program</th>
                      <th>Round 1 Score</th>
                      <th>Round 2 Score</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCandidates.map((candidate) => (
                    <tr key={candidate.id}>
                      <td>
                        <Input
                          type="checkbox"
                          checked={selectedCandidates.includes(candidate.id)}
                          onChange={() => handleSelectCandidate(candidate.id)}
                          disabled={processing}
                        />
                      </td>
                      <td>{candidate.id}</td>
                      <td>{candidate.applicant_name}</td>
                      <td style={{ fontSize: '0.85rem' }}>{candidate.applicant_email}</td>
                      <td style={{ fontSize: '0.85rem' }}>{candidate.program}</td>
                      <td>
                        <Badge color="info">
                          {Number(candidate.round1Score)?.toFixed(1) || 'N/A'}
                        </Badge>
                      </td>
                      <td>
                        <Badge color="info">
                          {Number(candidate.round2Score)?.toFixed(1) || 'N/A'}
                        </Badge>
                      </td>
                      <td>
                        <Badge color="success">{candidate.review_status}</Badge>
                      </td>
                    </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          color="success"
          onClick={() => handleFinalize('select')}
          disabled={processing || selectedCandidates.length === 0 || candidates.length === 0}
        >
          {processing ? <Spinner size="sm" /> : null}
          {' '}Shortlist Candidates ({selectedCandidates.length})
        </Button>
        <Button
          color="danger"
          onClick={() => handleFinalize('reject')}
          disabled={processing || selectedCandidates.length === 0 || candidates.length === 0}
        >
          {processing ? <Spinner size="sm" /> : null}
          {' '}Reject Candidates ({selectedCandidates.length})
        </Button>
        <Button color="secondary" onClick={toggle} disabled={processing}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default FinalizeCandidatesModal;
